import React, { createContext, useCallback, useContext, useState, ReactNode } from "react";
import { toast } from "sonner";
import { giftTiers, GiftItem } from "@/data/giftData";

export type ChosenGift = GiftItem & { selectedVariant?: string; tierId: string };

interface GiftContextType {
  /** tierId -> giftId */
  selectedGifts: Record<string, string>;
  /** tierId -> zvolený variant darčeka */
  selectedVariants: Record<string, string>;
  selectGift: (tierId: string, giftId: string, variant?: string) => void;
  clearGifts: () => void;
  /** Darčeky, ktoré sú vybrané A zároveň odomknuté pri danom medzisúčte */
  getChosenGifts: (subtotal: number) => ChosenGift[];
}

const GiftContext = createContext<GiftContextType | undefined>(undefined);

export const GiftProvider = ({ children }: { children: ReactNode }) => {
  const [selectedGifts, setSelectedGifts] = useState<Record<string, string>>({});
  const [selectedVariants, setSelectedVariants] = useState<Record<string, string>>({});

  const selectGift = useCallback((tierId: string, giftId: string, variant?: string) => {
    const gift = giftTiers.find((t) => t.id === tierId)?.gifts.find((g) => g.id === giftId);

    setSelectedGifts((prev) => {
      const isSame = prev[tierId] === giftId;
      // Rovnaký darček + rovnaký (alebo žiadny) variant => odobrať
      if (isSame && (!variant || selectedVariants[tierId] === variant)) {
        const next = { ...prev };
        delete next[tierId];
        setSelectedVariants((pv) => {
          const nv = { ...pv };
          delete nv[tierId];
          return nv;
        });
        toast.info("Darček odstránený");
        return next;
      }

      setSelectedVariants((pv) => {
        const nv = { ...pv };
        if (variant) nv[tierId] = variant;
        else delete nv[tierId];
        return nv;
      });

      if (gift) {
        toast.success("Darček pridaný zadarmo!", {
          description: variant ? `${gift.name} — ${variant}` : gift.name,
        });
      }
      return { ...prev, [tierId]: giftId };
    });
  }, [selectedVariants]);

  const clearGifts = useCallback(() => {
    setSelectedGifts({});
    setSelectedVariants({});
  }, []);

  const getChosenGifts = useCallback(
    (subtotal: number): ChosenGift[] =>
      giftTiers
        .filter((t) => selectedGifts[t.id] && subtotal >= t.threshold)
        .map((t) => {
          const gift = t.gifts.find((g) => g.id === selectedGifts[t.id]);
          return gift ? { ...gift, selectedVariant: selectedVariants[t.id], tierId: t.id } : undefined;
        })
        .filter(Boolean) as ChosenGift[],
    [selectedGifts, selectedVariants]
  );

  return (
    <GiftContext.Provider
      value={{ selectedGifts, selectedVariants, selectGift, clearGifts, getChosenGifts }}
    >
      {children}
    </GiftContext.Provider>
  );
};

export const useGifts = () => {
  const ctx = useContext(GiftContext);
  if (!ctx) throw new Error("useGifts must be used within a GiftProvider");
  return ctx;
};
